<template>
  <v-table>
    <thead>
      <th
        v-for="header in headers"
        :key="header.key"
        :class="['cell', header.class]"
        @click="handleSort(header.key)"
      >
        {{ header.label }}
        <span v-if="sortKey === header.key" class="sort-indicator">
          {{ sortDirection === "asc" ? "↑" : "↓" }}
        </span>
      </th>
    </thead>
    <tbody>
      <tr
        @click="
          isOpenEditModel
            ? handleAction('edit', row)
            : viewFile
            ? handleAction('view', row)
            : route(row[id])
        "
        v-for="(row, index) in sortedAndFilteredData"
        :key="index"
        class="line"
      >
        <td
          v-for="header in headers"
          :key="header.key"
          :class="['cell', header.class]"
        >
          <template v-if="header.type === 'actions'">
            <div class="actions-container">
              <span
                v-for="action in actions"
                :key="action.icon"
                class="action-icon"
                @click.stop="handleAction(action.type, row)"
                :title="action.label"
              >
                <v-icon
                  v-if="
                    (action.type !== 'copy' || downloadBtn) &&
                    (action.type !== 'edit' || editBtn)
                  "
                  color="#BFBFBF"
                  :icon="action.icon"
                ></v-icon>
              </span>
            </div>
          </template>
          <template v-else-if="header.type === 'date'">
            {{ formatDate(row[header.key]) }}
          </template>
          <template v-else-if="row?.hasOwnProperty('icon') && header?.isIcon">
            <v-icon :color="row.iconColor" :icon="row.icon"></v-icon>
            {{ row[header.key] }}
          </template>
          <template v-else-if="header.type === 'boolean'">
            <div>{{ row[header.key] ? "כן" : "לא" }}</div>
          </template>
          <template v-else-if="header.type === 'longText'">
            <div>{{ truncateText(row[header.key]) }}</div>
          </template>

          <template v-else>
            {{ row[header.key] }}
          </template>
        </td>
      </tr>
    </tbody>
  </v-table>
</template>

<script>
import { dateToDisplay } from "@/dates";
import { truncateText } from "@/utilities";
export default {
  emits: ["action"],
  props: {
    headers: {
      type: Array,
      required: true,
    },
    data: {
      type: Array,
      required: true,
    },
    searchQuery: {
      type: String,
      required: false,
    },
    downloadBtn: {
      type: Boolean,
      default: true,
    },
    editBtn: {
      type: Boolean,
      default: true,
    },
    id: {
      type: String,
      required: false,
    },
    isOpenEditModel: {
      type: Boolean,
      default: true,
    },
    viewFile: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sortDirection: "asc",
      actions: [
        {
          type: "copy",
          icon: "mdi-download",
          label: "העתק",
          hoverColor: "blue",
        },
        {
          type: "edit",
          icon: "mdi-pencil",
          label: "ערוך",
          hoverColor: "yellow",
        },
        {
          type: "delete",
          icon: "mdi-trash-can",
          label: "ביטול",
          hoverColor: "blue",
        },
      ],
    };
  },
  computed: {
    sortedAndFilteredData() {
      let filteredData = [...this.data];

      // Apply search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filteredData = filteredData.filter((row) =>
          Object.values(row).some((value) =>
            String(value).toLowerCase().includes(query)
          )
        );
      }

      // Apply sorting
      if (this.sortKey) {
        filteredData.sort((a, b) => {
          const aVal = a[this.sortKey];
          const bVal = b[this.sortKey];

          // Handle different types of values
          if (
            this.headers.find((h) => h.key === this.sortKey)?.type === "date"
          ) {
            return this.sortDirection === "asc"
              ? new Date(aVal) - new Date(bVal)
              : new Date(bVal) - new Date(aVal);
          }

          // Default string comparison
          return this.sortDirection === "asc"
            ? String(aVal).localeCompare(String(bVal))
            : String(bVal).localeCompare(String(aVal));
        });
      }

      return filteredData;
    },
  },
  methods: {
    route(index) {
      this.$emit("route", index);
    },
    handleSort(key) {
      if (this.sortKey === key) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = key;
        this.sortDirection = "asc";
      }
      this.$emit("sort", { key, direction: this.sortDirection });
    },

    handleAction(type, row) {
      this.$emit("action", { type, row });
    },
    truncateText(text) {
      return truncateText(text);
    },

    formatDate(date) {
      return dateToDisplay(date);
    },
  },
};
</script>

<style lang="scss">
@media only screen and (max-width: 601px) {
  .v-table__wrapper {
    overflow: scroll;
    max-height: 500px;
  }
  .cell {
    min-width: 130px !important;
  }
}
</style>
<style scoped lang="scss">
@media only screen and (max-width: 1440px) {
  thead th {
    font-size: 15px !important;
    min-width: 100px;
  }
  tbody td {
    font-size: 15px !important;
    min-width: 100px;
  }

  tbody td.details {
    min-width: 150px;
  }
}
thead {
  border-radius: 0px 20px 20px 0px;
  background-color: #f5f5f5;
  th {
    font-size: $mediumTextSize;
    font-weight: 400;
    padding: 15px 0px;

    &:first-child {
      border-radius: 0px 20px 20px 0px;
    }
    &:last-child {
      border-radius: 20px 0px 0px 20px;
    }
  }
}
tr {
  height: 70px;
  cursor: pointer;
  &:hover {
    background: #f8f8f8;
  }
}
td {
  color: $orderLineColor;
  font-size: $mediumTextSize;
  text-align: center !important;
}
.actions-container {
  display: flex;
  justify-content: center;
  gap: 10px;
  .action-icon {
    .mdi-download {
      &:hover {
        color: $cartMainColor !important;
      }
    }
    .mdi-pencil {
      &:hover {
        color: $pencilColor !important;
      }
    }
    .mdi-trash-can {
      &:hover {
        color: $customerMainColor !important;
      }
    }
  }
}
.cell {
  max-width: 150px;
}
tfoot {
  border-radius: 0px 20px 20px 0px;
  background-color: #f5f5f5;
  color: #868686;
  th {
    font-size: $mediumTextSize;
    font-weight: 700;
    padding: 15px 0px;

    &:first-child {
      border-radius: 0px 50px 50px 0px;
    }
    &:last-child {
      border-radius: 50px 0px 0px 50px;
    }
  }
  .font-weight-normal {
    font-weight: 400;
  }
}
</style>
